import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "wouter";
import { Slider } from "@/components/ui/slider";
import ArtworkDisplay from "@/components/artwork-display";
import Footer from "@/components/footer";
import SearchHeader from "@/components/search-header";
import SEOHead from "@/components/SEOHead";
import {
  loadEmotionData,
  loadAllArtworks,
  StaticArtwork,
  EmotionData,
} from "@/lib/static-data";
import { searchArtworks } from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";
import { SiInstagram } from "react-icons/si";
import { FaXTwitter } from "react-icons/fa6";
import { Link as LinkIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Define the 10 emotions with descriptions
const EMOTIONS = [
  {
    key: "Valence",
    label: "Valence (Pleasantness)",
    description: "Positive vs. negative feeling",
  },
  {
    key: "Arousal",
    label: "Arousal (Intensity)",
    description: "Calm vs. highly stimulating",
  },
  {
    key: "Dominance",
    label: "Dominance (Control)",
    description: "Feeling empowered vs. overwhelmed",
  },
  {
    key: "Sadness",
    label: "Sadness (Melancholy)",
    description: "Evocation of grief, loss, longing",
  },
  {
    key: "Joy",
    label: "Joy (Serenity)",
    description: "Happiness, peace, uplifting calm",
  },
  {
    key: "Tension",
    label: "Tension (Anxiety)",
    description: "Unease, fear, suspense",
  },
  {
    key: "Anger",
    label: "Anger (Defiance)",
    description: "Rage, rebellion, provocation",
  },
  {
    key: "Nostalgia",
    label: "Nostalgia",
    description: "Sentimentality, memory, longing for the past",
  },
  {
    key: "Wonder",
    label: "Wonder (Awe)",
    description: "Feeling of transcendence, vastness, sublime",
  },
  {
    key: "Interest",
    label: "Interest (Curiosity)",
    description: "Engagement, stimulation of thought",
  },
];

// Cosine similarity calculation
function cosineSimilarity(vectorA: number[], vectorB: number[]): number {
  if (vectorA.length !== vectorB.length) return 0;

  let dotProduct = 0;
  let magnitudeA = 0;
  let magnitudeB = 0;

  for (let i = 0; i < vectorA.length; i++) {
    dotProduct += vectorA[i] * vectorB[i];
    magnitudeA += vectorA[i] * vectorA[i];
    magnitudeB += vectorB[i] * vectorB[i];
  }

  magnitudeA = Math.sqrt(magnitudeA);
  magnitudeB = Math.sqrt(magnitudeB);

  if (magnitudeA === 0 || magnitudeB === 0) return 0;

  return dotProduct / (magnitudeA * magnitudeB);
}

export default function EmotionSearchPage() {
  const params = useParams();
  const { toast } = useToast();
  const [emotionSliders, setEmotionSliders] = useState<Record<string, number>>(
    Object.fromEntries(EMOTIONS.map((emotion) => [emotion.key, 50])),
  );
  const [emotionData, setEmotionData] = useState<Map<number, EmotionData[]>>(
    new Map(),
  );
  const [artworks, setArtworks] = useState<StaticArtwork[]>([]);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initializedFromArtwork, setInitializedFromArtwork] = useState(false);

  // Load data on mount and initialize sliders from URL if coming from artwork page
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [emotionMap, artworksData] = await Promise.all([
          loadEmotionData(),
          loadAllArtworks(),
        ]);
        setEmotionData(emotionMap);
        setArtworks(artworksData);

        let artworkId;
        if (params.id) {
          artworkId = parseInt(params.id);
        } else {
          artworkId = 1234;
        }

        console.log("Current URL:", window.location.href); // Debug log
        console.log("Path parameter 'id':", params.id); // Debug log
        console.log("Parsed artwork ID:", artworkId); // Debug log

        if (artworkId && !initializedFromArtwork) {
          const artworkEmotions = emotionMap.get(artworkId);
          console.log(
            "Found emotions for artwork",
            artworkId,
            ":",
            artworkEmotions,
          ); // Debug log

          if (artworkEmotions) {
            const newSliders: Record<string, number> = {};
            EMOTIONS.forEach((emotion) => {
              const emotionValue = artworkEmotions.find(
                (e) => e.emotion === emotion.key,
              );
              newSliders[emotion.key] = emotionValue
                ? Math.round(emotionValue.value)
                : 50;
            });
            console.log("Setting sliders to:", newSliders); // Debug log
            setEmotionSliders(newSliders);
            setInitializedFromArtwork(true);
            trackEvent(
              "initialization",
              "emotion_sliders_from_artwork",
              params.id,
              artworkId,
            );
          }
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []); // Remove dependency to prevent re-running

  // Calculate closest artworks based on user's emotion vector
  const closestArtworks = useMemo(() => {
    if (emotionData.size === 0 || artworks.length === 0) return [];

    const userVector = EMOTIONS.map((emotion) => emotionSliders[emotion.key]);
    const similarities: Array<{ artwork: StaticArtwork; similarity: number }> =
      [];

    // Find artworks that have emotion data and calculate similarity
    for (const artwork of artworks) {
      if (!artwork.id) continue;

      const artworkEmotions = emotionData.get(artwork.id);
      if (!artworkEmotions) continue;

      // Create emotion vector for this artwork
      const artworkVector = EMOTIONS.map((emotion) => {
        const emotionValue = artworkEmotions.find(
          (e) => e.emotion === emotion.key,
        );
        return emotionValue ? emotionValue.value : 50; // Default to 50 if emotion not found
      });

      const similarity = cosineSimilarity(userVector, artworkVector);
      similarities.push({ artwork, similarity });
    }

    // Sort by similarity (highest first) and return top 5 (1 main + 4 similar)
    return similarities
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5)
      .map((item) => item.artwork);
  }, [emotionSliders, emotionData, artworks]);

  const handleSliderChange = (emotionKey: string, value: number[]) => {
    setEmotionSliders((prev) => ({
      ...prev,
      [emotionKey]: value[0],
    }));

    trackEvent("interaction", "emotion_slider_change", emotionKey, value[0]);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const results = searchArtworks(artworks, query, 20);
    setSearchResults(results);
    trackEvent("search", "artwork_search_emotion_page", query, results.length);
  };

  const mainArtwork = closestArtworks[0];
  const similarArtworks = closestArtworks.slice(1, 5);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Emotion Search - Ariadne's Gallery"
        description="Search for artworks by the emotions they evoke. Use interactive sliders to find art that matches your desired emotional experience."
      />

      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={(artworkId) => {
          // Navigate to artwork page or handle selection
          window.location.href = `/artwork/${artworkId}/`;
        }}
        onSearch={handleSearch}
        totalCount={artworks.length}
        onRandomArtwork={() => {
          if (artworks.length > 0) {
            const randomArtwork =
              artworks[Math.floor(Math.random() * artworks.length)];
            if (randomArtwork.id) {
              window.location.href = `/artwork/${randomArtwork.id}/`;
            }
          }
        }}
        currentArtworkId={mainArtwork?.id || undefined}
        currentArtwork={mainArtwork || null}
      />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Loading emotion data...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left side - Main artwork (8 columns) */}
              <div className="lg:col-span-8 space-y-6">
                {mainArtwork ? (
                  <div>
                    <Link href={`/artwork/${mainArtwork.id}/`}>
                      <h2 className="text-2xl font-semibold mb-4 cursor-pointer hover:text-blue-600 transition-colors">
                        {mainArtwork.title}
                      </h2>
                    </Link>
                    <p className="text-gray-600 mb-4">{mainArtwork.artist}</p>
                    <div className="w-full">
                      <ArtworkDisplay artwork={mainArtwork} showTitle={false} />
                    </div>
                    
                    <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          const currentUrl = window.location.href;
                          const twitterText = `Discover art through emotions at @ariadnesgallery - Try this emotion search`;
                          const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(currentUrl)}`;
                          window.open(twitterUrl, '_blank', 'width=600,height=400,noopener,noreferrer');
                        }}
                        className="p-2 bg-black hover:bg-gray-800 text-white rounded-full transition-colors"
                        data-testid="button-share-twitter-emotion-search"
                        aria-label="Share emotion search on Twitter"
                        title="Share on Twitter"
                      >
                        <FaXTwitter className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const currentUrl = window.location.href;
                          const instagramText = `Discover art through emotions! Follow ariadnes.gallery for more emotion-based art discoveries. ${currentUrl}`;
                          navigator.clipboard.writeText(instagramText).then(() => {
                            toast({
                              title: "Copied to clipboard!",
                              description: "Instagram share text copied successfully.",
                            });
                          }).catch(() => {
                            toast({
                              title: "Copy failed",
                              description: "Please manually copy the URL to share.",
                              variant: "destructive",
                            });
                          });
                        }}
                        className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full transition-colors"
                        data-testid="button-share-instagram-emotion-search"
                        aria-label="Copy Instagram share text for emotion search"
                        title="Copy for Instagram"
                      >
                        <SiInstagram className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const currentUrl = window.location.href;
                          navigator.clipboard.writeText(currentUrl).then(() => {
                            toast({
                              title: "Link copied!",
                              description: "Emotion search link copied to clipboard.",
                            });
                          }).catch(() => {
                            toast({
                              title: "Copy failed",
                              description: "Please manually copy the URL.",
                              variant: "destructive",
                            });
                          });
                        }}
                        className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-full transition-colors"
                        data-testid="button-share-link-emotion-search"
                        aria-label="Copy emotion search link"
                        title="Copy link"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <p className="text-gray-500">
                      Adjust the emotion sliders to find matching artworks
                    </p>
                  </div>
                )}

                {/* Similar artworks grid */}
                {similarArtworks.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">
                      Similar Artworks
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      {similarArtworks.map((artwork, index) => (
                        <Link
                          key={artwork.id || index}
                          href={`/artwork/${artwork.id}/`}
                          className="group block"
                        >
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={artwork.url || "/api/placeholder/300/300"}
                              alt={artwork.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              loading="lazy"
                            />
                          </div>
                          <div className="mt-2">
                            <h4 className="text-sm font-medium truncate">
                              {artwork.title}
                            </h4>
                            <p className="text-xs text-gray-500 truncate">
                              {artwork.artist}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right side - Emotion sliders (4 columns) */}
              <div className="lg:col-span-4 space-y-6">
                <div className="text-center lg:text-left">
                  <div className="flex items-baseline justify-between mb-2">
                    <h1 className="text-2xl font-semibold">
                      <wbr />
                    </h1>
                    <Link href="/emotion" className="text-sm text-indigo-600 hover:underline shrink-0">
                      Browse collections
                    </Link>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Adjust the sliders to define your desired emotional
                    experience and find art that resonates with those feelings.
                  </p>
                </div>
                <div className="space-y-4">
                  {EMOTIONS.map((emotion) => (
                    <div key={emotion.key} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium">
                            {emotion.label}
                          </label>
                          <span className="text-xs text-gray-500">
                            — {emotion.description}
                          </span>
                        </div>
                        <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-xs">
                          {emotionSliders[emotion.key]}
                        </span>
                      </div>
                      <Slider
                        value={[emotionSliders[emotion.key]]}
                        onValueChange={(value) =>
                          handleSliderChange(emotion.key, value)
                        }
                        max={100}
                        min={0}
                        step={1}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
